<template>
  <div class="signup-tutorial">
    <h1 class="section-title">{{ t('get_started_in_steps_header') }}</h1>
    <NumberedList class="tutorial-list" :items="tutorialIitems" />
    <ul class="improvement-stat-list">
      <li class="improvement-stat" v-for="(stat, index) in improvementStatistics" :key="index">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="23"
          height="23"
          viewBox="0 0 23 23"
          fill="none"
          class="stat-icon"
          v-html="stat.innerSvg"
        ></svg>
        <h4 class="stat-title">{{ t(stat.title) }}</h4>
        <p class="stat-subtitle">{{ t(stat.subtitle) }}</p>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import NumberedList from '@/components/common/NumberedList.vue'

const { t } = useI18n()
const tutorialIitems = [
  {
    title: t('create_your_account'),
    subtitle: t('signup_fill_out_form'),
  },
  {
    title: t('set_up_your_automations'),
    subtitle: t('choose_how_to_make_impact'),
  },
  {
    title: t('thats_it'),
    subtitle: t('account_is_ready'),
  },
]

const improvementStatistics = [
  {
    title: 'customer_engagement_increase_title',
    subtitle: 'customer_engagement_increase_subtitle',
    innerSvg: `<path
      d="M13.0821 6.68305C13.0821 7.86111 12.1271 8.81613 10.949 8.81613C9.77098 8.81613 8.81597 7.86111 8.81597 6.68305C8.81597 5.50499 9.77098 4.54998 10.949 4.54998C12.1271 4.54998 13.0821 5.50499 13.0821 6.68305Z"
      stroke="#47D18F"
      stroke-width="0.9"
    />
    <path
      d="M7.9161 14.7545C7.9161 15.9325 6.96109 16.8875 5.78303 16.8875C4.60496 16.8875 3.64995 15.9325 3.64995 14.7545C3.64995 13.5764 4.60496 12.6214 5.78303 12.6214C6.96109 12.6214 7.9161 13.5764 7.9161 14.7545Z"
      stroke="#47D18F"
      stroke-width="0.9"
    />
    <path
      d="M18.2483 14.7545C18.2483 15.9325 17.2932 16.8875 16.1152 16.8875C14.9371 16.8875 13.9821 15.9325 13.9821 14.7545C13.9821 13.5764 14.9371 12.6214 16.1152 12.6214C17.2932 12.6214 18.2483 13.5764 18.2483 14.7545Z"
      stroke="#47D18F"
      stroke-width="0.9"
    />
    <path
      d="M9.49615 8.78229L7.23596 12.4955"
      stroke="#47D18F"
      stroke-width="0.9"
      stroke-linecap="round"
    />
    <path
      d="M13.6813 15.0547L8.0432 15.0777"
      stroke="#47D18F"
      stroke-width="0.9"
      stroke-linecap="round"
    />
    <path
      d="M12.2759 8.94306L14.5988 12.6173"
      stroke="#47D18F"
      stroke-width="0.9"
      stroke-linecap="round"
    />`,
  },
  {
    title: 'conversion_rate_increase_title',
    subtitle: 'conversion_rate_increase_subtitle',
    innerSvg: `<path
      d="M7.4049 16.8084V14.3258C7.4049 13.4689 6.71021 12.7742 5.85327 12.7742V12.7742C4.99632 12.7742 4.30164 13.4689 4.30164 14.3258V16.8084"
      stroke="#47D18F"
      stroke-width="0.9"
    />
    <path
      d="M9.88757 16.8076V10.5235C9.88757 9.79507 10.4781 9.20459 11.2065 9.20459V9.20459C11.9349 9.20459 12.5253 9.79508 12.5253 10.5235V16.8076"
      stroke="#47D18F"
      stroke-width="0.9"
    />
    <path
      d="M15.1631 16.963V6.10162C15.1631 5.24468 15.8578 4.54999 16.7147 4.54999V4.54999C17.5717 4.54999 18.2663 5.24468 18.2663 6.10162V16.963"
      stroke="#47D18F"
      stroke-width="0.9"
    />
    <path d="M2.75 18.05H19.9731" stroke="#47D18F" stroke-width="0.9" stroke-linecap="round" />
    <path
      d="M4.09546 9.20316L7.97454 5.47925M7.97454 5.47925H4.09546M7.97454 5.47925V9.048"
      stroke="#47D18F"
      stroke-width="0.9"
      stroke-linejoin="round"
    />`,
  },

  {
    title: 'rise_in_order_value_title',
    subtitle: 'rise_in_order_value_subtitle',
    innerSvg: `<path
      d="M4.55005 17.91H18.36M5.0812 10.4738L8.26812 7.81805L10.3927 9.41151L17.8289 4.09998M5.0812 14.1919L8.26812 11.5361L10.3927 13.1296L17.8289 7.81805V15.2499H5.08121L5.0812 14.1919Z"
      stroke="#47D18F"
      stroke-width="0.9"
      stroke-linecap="round"
      stroke-linejoin="round"
    />`,
  },

  {
    title: 'campaign_revenue_title',
    subtitle: 'campaign_revenue_subtitle',
    innerSvg: `<circle
      cx="4.45833"
      cy="18.1603"
      r="0.675"
      fill="#47D18F"
      stroke="#47D18F"
      stroke-width="1.16651"
    />
    <path
      d="M5.39835 16.5838L5.08016 16.902L5.71655 17.5384L6.03475 17.2202L5.39835 16.5838ZM6.53805 16.7169C6.71379 16.5412 6.71379 16.2563 6.53805 16.0805C6.36232 15.9048 6.07739 15.9048 5.90166 16.0805L6.53805 16.7169ZM6.03475 17.2202L6.53805 16.7169L5.90166 16.0805L5.39835 16.5838L6.03475 17.2202Z"
      fill="#47D18F"
    />
    <circle
      cx="12.8612"
      cy="15.3538"
      r="0.675"
      transform="rotate(-91.4948 12.8612 15.3538)"
      fill="#47D18F"
      stroke="#47D18F"
      stroke-width="1.16651"
    />
    <path
      d="M11.3931 14.7479L11.7045 15.0728L12.3543 14.4501L12.0429 14.1252L11.3931 14.7479ZM10.8363 12.8661C10.6643 12.6866 10.3795 12.6806 10.2 12.8525C10.0206 13.0245 10.0145 13.3093 10.1865 13.4888L10.8363 12.8661ZM12.0429 14.1252L10.8363 12.8661L10.1865 13.4888L11.3931 14.7479L12.0429 14.1252Z"
      fill="#47D18F"
    />
    <circle
      cx="18.0852"
      cy="9.61921"
      r="0.675"
      transform="rotate(177.36 18.0852 9.61921)"
      fill="#47D18F"
      stroke="#47D18F"
      stroke-width="1.16651"
    />
    <path
      d="M17.5163 11.0909L17.8271 10.7655L17.1764 10.1438L16.8655 10.4692L17.5163 11.0909ZM12.9406 14.5775C12.7689 14.7572 12.7754 15.0421 12.9551 15.2138C13.1348 15.3854 13.4197 15.3789 13.5913 15.1992L12.9406 14.5775ZM16.8655 10.4692L12.9406 14.5775L13.5913 15.1992L17.5163 11.0909L16.8655 10.4692Z"
      fill="#47D18F"
    />
    <circle
      cx="9.96307"
      cy="12.6546"
      r="0.675"
      transform="rotate(178.576 9.96307 12.6546)"
      fill="#47D18F"
      stroke="#47D18F"
      stroke-width="1.16651"
    />
    <path
      d="M9.55775 13.6972L9.87595 13.379L9.23956 12.7426L8.92136 13.0608L9.55775 13.6972ZM7.41142 14.5707C7.23568 14.7464 7.23568 15.0314 7.41142 15.2071C7.58715 15.3828 7.87208 15.3828 8.04781 15.2071L7.41142 14.5707ZM8.92136 13.0608L7.41142 14.5707L8.04781 15.2071L9.55775 13.6972L8.92136 13.0608Z"
      fill="#47D18F"
    />
    <circle cx="12.7627" cy="6.83598" r="3.57641" stroke="#47D18F" stroke-width="0.9" />
    <path
      d="M12.3106 8.34707C12.1872 8.34707 12.0602 8.33473 11.9295 8.31004C11.7988 8.28681 11.6776 8.2585 11.5658 8.2251C11.454 8.19025 11.3654 8.15686 11.3 8.12491L11.3654 7.5325C11.467 7.58186 11.573 7.6276 11.6834 7.66971C11.7937 7.71182 11.907 7.74594 12.0231 7.77208C12.1393 7.79821 12.2569 7.81128 12.376 7.81128C12.5241 7.81128 12.6431 7.78224 12.7332 7.72416C12.8232 7.66608 12.8682 7.57751 12.8682 7.45844C12.8682 7.36697 12.8428 7.29364 12.792 7.23847C12.7412 7.18184 12.6635 7.13174 12.5589 7.08818C12.4544 7.04317 12.3208 6.99235 12.1582 6.93572C11.9956 6.8791 11.8496 6.81521 11.7204 6.74406C11.5912 6.67146 11.4888 6.57998 11.4133 6.46963C11.3378 6.35928 11.3 6.21844 11.3 6.0471C11.3 5.87867 11.3414 5.72911 11.4242 5.59843C11.507 5.4663 11.6304 5.36321 11.7945 5.28916C11.96 5.21365 12.1654 5.1759 12.4108 5.1759C12.5967 5.1759 12.7738 5.19695 12.9423 5.23906C13.1107 5.27972 13.2544 5.324 13.3735 5.37192L13.3169 5.94256C13.1455 5.8656 12.9844 5.80752 12.8334 5.76832C12.6838 5.72766 12.5313 5.70733 12.376 5.70733C12.2134 5.70733 12.0878 5.73419 11.9992 5.78792C11.9106 5.84164 11.8663 5.92223 11.8663 6.02968C11.8663 6.11534 11.891 6.18431 11.9404 6.23659C11.9897 6.28886 12.0602 6.33387 12.1516 6.37162C12.2431 6.40792 12.3527 6.4464 12.4805 6.48706C12.7012 6.55675 12.8864 6.63371 13.0359 6.71792C13.1869 6.80069 13.3002 6.9016 13.3757 7.02067C13.4526 7.13828 13.4911 7.2842 13.4911 7.45844C13.4911 7.54556 13.4773 7.64067 13.4497 7.74376C13.4221 7.8454 13.3677 7.94196 13.2864 8.03344C13.2051 8.12491 13.086 8.20042 12.9292 8.25995C12.7738 8.31803 12.5676 8.34707 12.3106 8.34707ZM12.2453 8.91335L12.2889 8.13798H12.6286L12.6722 8.91335H12.2453ZM12.2889 5.3545L12.2453 4.60962H12.6722L12.6286 5.3545H12.2889Z"
      fill="#47D18F"
    />`,
  },
]
</script>

<style lang="scss" scoped>
.signup-tutorial {
  background-color: var(--ui-dark-gray);
  color: var(--ui-white);
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 32px;
}

.tutorial-list {
  flex-grow: 1;
  flex-shrink: 1;
  padding: 0;
  margin-bottom: 32px;
}

.improvement-stat-list {
  list-style: none;
  gap: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
}

.improvement-stat {
  background-color: var(--opaque-background);
  padding: 8px 16px;
  gap: 4px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  .stat-title,
  .stat-subtitle {
    margin: 0;
  }

  .stat-icon {
    width: 22px;
    height: 22px;
  }

  .stat-title {
    font-size: 16px;
    font-weight: 700;
    line-height: normal;
  }

  .stat-subtitle {
    font-size: 14px;
    font-weight: 400;
    line-height: normal;
  }
}
</style>
